/**
 * Provider management — wire types, trust-boundary guards, and the read↔edit
 * round-trip (ADR-040 §D-1 / §D-10).
 *
 * Pure, React-free.
 *
 * # Two shapes from one endpoint
 *
 * ``GET /api/admin/providers/…`` is readable by **every authenticated role**
 * (REQ-UI-165) but is **field-limited** by role: bind endpoints, demarcation IPs,
 * cert-material references, the conversion profile ref and filter *literals* are
 * `admin` / `support_engineer` only, because they describe collection posture
 * (ADR-040 §D-10). The BFF projects accordingly and is the enforcement point.
 *
 * **This module therefore renders what arrived, and never re-derives the
 * field-set from the session's roles.** Re-deriving would put a second,
 * independent copy of a security rule in the client where it can drift from the
 * server's; and it would be a lie either way — a privileged field the BFF
 * withheld cannot be shown however the client feels about the session, and one
 * it sent is already disclosed. Privileged fields are consequently *optional* on
 * :type:`ProviderInterfaceView`, and :func:`isPrivilegedProviderInterfaceView`
 * reports what the response actually carried.
 *
 * # Why hand-rolled guards, not zod
 *
 * The routes return :class:`JSONResponse`, so their response schema is ``{}`` in
 * the committed ``openapi.yaml`` and ``openapi-typescript`` emits no type for
 * it. ``zod`` is not a UI dependency (the tech-stack add is deferred per
 * CLAUDE.md §4.3), so this mirrors the established hand-rolled guard pattern of
 * :file:`@/peers/peer-model` and :file:`@/system-status/status-model`: a drifted
 * payload is rejected loudly rather than rendered as a half-populated ingest
 * path (CLAUDE.md §8.1).
 *
 * **Request** bodies are the opposite case — ``ProviderInterfaceConfig`` *is* on
 * the OpenAPI surface, so the write path uses the generated type verbatim.
 *
 * Cross-references:
 *   - ADR-040 §D-1 (the three-level model), §D-4 (per-interface trust),
 *     §D-5 (conversion), §D-6 (the allow-all filter), §D-10 (RBAC).
 *   - REQ-PROVIDER-004 / -007 / -010 / -016 / -028 / -029 / -037 / -040.
 *   - `packages/meridian_bff/src/meridian_bff/_provider_routes.py` (the projections).
 */
import type { components } from "@/lib/api-types.gen";
import {
  findProviderInterfaceType,
  PROJECTED_FROM_AGGREGATOR_KEY,
} from "@/providers/provider-catalog";
import type {
  ConversionMode,
  InterfaceState,
  ProviderConnectionRole,
  TransportSecurity,
  TrustMode,
} from "@/providers/provider-catalog";

// ---- Request types (generated — the write path has no hand-written shape) ----

/** The PUT body: a complete Provider Interface (ADR-040 §D-1). */
export type ProviderInterfaceConfig = components["schemas"]["ProviderInterfaceConfig"];
/** One provider connection within a PUT body. */
export type ProviderInterfaceConnectionConfig =
  components["schemas"]["ProviderInterfaceConnectionConfig"];
/** The provider PUT body: one external system that feeds this deployment. */
export type ProviderConfig = components["schemas"]["ProviderConfig"];
/** The versioned filter document (ADR-040 §D-6) — shared shape with egress. */
export type ProviderFilterConfig = components["schemas"]["InterfaceFilterConfig"];
/** How records are delimited on this interface's wire. */
export type Framing = components["schemas"]["Framing"];

/** The filter document's schema version this build authors. */
export const FILTER_SCHEMA_VERSION = 1;
/** The interface/provider-config schema version this build authors. */
export const PROVIDER_SCHEMA_VERSION = 1;

/**
 * Default bound on one framed record — 12 MiB, mirroring the server default and
 * the connector framework's ``DEFAULT_MAX_FRAME_BYTES``.
 *
 * Restated here because the UI has no import path to either; it is the value a
 * newly-created interface starts at and the fallback when a read view predates
 * the field.
 */
export const DEFAULT_MAX_FRAME_BYTES = 12 * 1024 * 1024;

/**
 * The port a brand-new connection is born with.
 *
 * A deliberate non-zero placeholder rather than 0: port 0 means "let the kernel
 * choose" to a listener, which on an ingress bind is a socket no provider can
 * ever be told how to reach. The operator is expected to set this; a value that
 * would silently *work* and be unreachable is the worse default.
 */
export const DEFAULT_PROVIDER_PORT = 9443;

// ---- Response types (hand-authored — the routes return JSONResponse) --------

/**
 * One provider connection as the list/get endpoints project it.
 *
 * Everything from ``endpoint_host`` down is **privileged-only** — the BFF omits
 * the whole block for operator / analyst sessions, because a bind host and port
 * say *where Meridian listens*, which is the perimeter's attack-surface address
 * (REQ-UI-165).
 */
export interface ProviderInterfaceConnectionView {
  connection_id: string;
  name: string;
  state: InterfaceState;
  role: ProviderConnectionRole;
  endpoint_host?: string | null;
  endpoint_port?: number;
  timeout_seconds?: number;
  retry_max_attempts?: number;
  retry_backoff_seconds?: number;
  client_fingerprint_sha256?: string | null;
  filter?: ProviderFilterConfig | null;
}

/**
 * The non-disclosing description of a filter every role sees.
 *
 * ⚠ ``configured: false`` on THIS surface means the interface **receives
 * everything** (REQ-PROVIDER-037's allow-all fail-safe) — the exact inverse of
 * the peer summary's reading, where it means the interface emits nothing. The
 * summary reports the fact; the UI states the direction's meaning
 * (REQ-UI-160), and both surprising readings get said out loud.
 */
export interface ProviderFilterSummary {
  configured: boolean;
  clause_count: number;
  selector_kinds: string[];
}

/** One Provider Interface as the list/get endpoints project it (both field-sets). */
export interface ProviderInterfaceView {
  interface_id: string;
  name: string;
  type_id: string;
  enabled: boolean;
  state: InterfaceState;
  connections: ProviderInterfaceConnectionView[];
  filter_summary: ProviderFilterSummary;
  /**
   * What the wire does (REQ-PROVIDER-029).
   *
   * **All-roles, and deliberately so.** It says *whether mission data arrives in
   * the clear*, which is the fact a non-privileged reader most needs — a
   * plaintext ingest path nobody can see is the sharpest case of "an operator
   * who cannot see these flows cannot notice one running that should not be".
   * It leaks nothing the privileged block guards: a posture is not an endpoint,
   * a credential path or a filter literal.
   */
  transport_security: TransportSecurity;
  /**
   * How an admitted connection is *attributed* (REQ-PROVIDER-028).
   *
   * **All-roles**, and the reason has no egress analogue: ``tunnel`` means
   * Meridian is **not authenticating this source itself** — the network layer
   * is, and every message on the connection is attributed to the configured
   * provider statically (ADR-023). An operator who cannot tell a
   * tunnel-attributed feed from an mTLS-authenticated one cannot judge how much
   * the ``producer_id`` on a record is worth, and REQ-PROVIDER-032 makes that
   * judgement their business. It discloses no demarcation address and no CA
   * path; those stay privileged.
   */
  trust_mode: TrustMode;
  /**
   * Which decoder produced the canonical record (REQ-PROVIDER-040, REQ-UI-162).
   *
   * **All-roles**, alongside its two siblings. Under ``legacy`` the proven
   * built-in decoder is authoritative, under ``engine`` an installed conversion
   * profile is; an operator who cannot tell them apart cannot notice a wire
   * running on a mapping that was never promoted.
   */
  conversion_mode: ConversionMode;
  // ---- privileged-only (absent for operator / analyst) ----
  /**
   * The installed (provider schema, conversion spec) pair this interface decodes
   * through.
   *
   * **Privileged**, unlike the mode above. The mode is a posture; this names a
   * specific artifact pair by digest — which mapping this site runs — and that
   * is deployment shape.
   */
  conversion_profile_ref?: string | null;
  /**
   * The owning provider — **required to build a write body**.
   *
   * ``PutProviderInterfaceRequest`` is ``{provider_id, interface}``, so an edit
   * cannot be saved without it. It is optional here rather than required because
   * it arrives only in the privileged block: a field-limited reader legitimately
   * has no parentage, and rejecting the whole list over a field only the write
   * path needs would blank every interface from the roster to protect a
   * capability that reader does not have.
   */
  provider_id?: string;
  /** Which registered listener carries this interface, for the one type that permits a choice. */
  transport_id?: string | null;
  framing?: Framing | null;
  framing_delimiter_hex?: string | null;
  framing_terminator?: string | null;
  max_frame_bytes?: number;
  /**
   * Where the network-layer-protected path admits from, under ``tunnel`` trust.
   *
   * ⚠ **An operational record, never a start gate** (architect ruling
   * 2026-08-26). Assembly imposes no non-empty check, the enforcement it
   * describes belongs to the deployment network layer, and the addresses are not
   * checked per frame either way. An **empty** list on a tunnel interface is a
   * legitimate, accepted configuration — and the UI says so in place, because a
   * list that looks like an allow-list and is not is worse than no list.
   *
   * Privileged: unlike the mode, these *are* deployment shape.
   */
  demarcation_ips?: string[];
  provider_ca_bundle_ref?: string | null;
  meridian_cert_ref?: string | null;
  meridian_key_ref?: string | null;
  filter?: ProviderFilterConfig | null;
  metadata?: Record<string, string>;
  schema_version?: number;
  created_by?: string;
}

/**
 * Whether the interface response carried the privileged field-set.
 *
 * Keyed on ``metadata``: it is the one privileged field
 * ``_interface_to_dict(full=True)`` **always** writes (``dict(record.metadata)``
 * — possibly empty, never omitted), so unlike ``filter`` or
 * ``provider_ca_bundle_ref`` it cannot be absent-because-null. A privilege test
 * a null value could flip would silently downgrade a fully privileged view of an
 * interface that simply has no filter yet.
 *
 * ⚠ Verified against the projection before reuse, not assumed from the egress
 * twin: ``_provider_routes._interface_to_dict`` writes ``metadata`` inside the
 * ``full`` block unconditionally, exactly as the peer one does. ``demarcation_ips``
 * would have been the other candidate (also unconditional, ``list(...)``), but an
 * empty list is a *meaningful* state on this surface, and a privilege key whose
 * empty value is also a real configuration is a key waiting to be mis-read.
 */
export function isPrivilegedProviderInterfaceView(view: ProviderInterfaceView): boolean {
  return view.metadata !== undefined;
}

// ---- Trust-boundary guards -------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Parse a provider interface list body.
 *
 * Rejects — rather than repairs — a payload that does not carry the fields every
 * role is promised. A half-parsed ingest list is the failure mode this feature
 * exists to prevent: an interface silently dropped from the list is a feed an
 * operator cannot see, and one with a guessed ``state`` is a kill switch showing
 * the wrong position. On ingress that is sharper than on egress — a stopped
 * ingress path produces no backlog and no complaint, only silence.
 *
 * @throws Error when the body is not ``{items: [...]}`` of well-formed interfaces.
 */
export function parseProviderInterfaceList(body: unknown): ProviderInterfaceView[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error("provider interface list: expected an object with an `items` array");
  }
  return body.items.map(parseProviderInterface);
}

/**
 * Parse one interface object from a list or single-interface body.
 *
 * @throws Error when a field every role is promised is missing or mistyped.
 */
export function parseProviderInterface(body: unknown): ProviderInterfaceView {
  if (!isRecord(body)) throw new Error("provider interface: expected an object");
  const { interface_id, name, type_id, enabled, connections, metadata } = body;
  if (typeof interface_id !== "string" || typeof name !== "string" || typeof type_id !== "string") {
    throw new Error("provider interface: interface_id, name and type_id must be strings");
  }
  if (typeof enabled !== "boolean") {
    throw new Error(`provider interface ${name}: enabled must be a boolean`);
  }
  if (!Array.isArray(connections)) {
    throw new Error(`provider interface ${name}: connections must be an array`);
  }
  const view: ProviderInterfaceView = {
    interface_id,
    name,
    type_id,
    enabled,
    state: parseInterfaceState(body.state, `interface ${name}`),
    connections: connections.map((c) => parseProviderConnection(c, name)),
    filter_summary: parseFilterSummary(body.filter_summary),
    // Parsed strictly rather than defaulted. A missing or unrecognised value on
    // any of the three means the client and the BFF disagree about the shape of
    // a security-relevant field, and silently substituting one would let the
    // editor round-trip a value the server never sent — rewriting a live
    // interface's wire security, its attribution, or its decoder on an
    // unrelated save.
    transport_security: parseTransportSecurity(body.transport_security, name),
    trust_mode: parseTrustMode(body.trust_mode, name),
    conversion_mode: parseConversionMode(body.conversion_mode, name),
  };
  // Privileged block — present as a unit or not at all. `metadata` decides (see
  // `isPrivilegedProviderInterfaceView`); the rest are copied through as-is
  // because their shapes are the generated request types the BFF echoes back.
  if (isRecord(metadata)) {
    view.metadata = metadata as Record<string, string>;
    if (typeof body.provider_id === "string") view.provider_id = body.provider_id;
    view.transport_id = asNullableString(body.transport_id);
    view.framing = asNullableString(body.framing) as Framing | null;
    view.framing_delimiter_hex = asNullableString(body.framing_delimiter_hex);
    view.framing_terminator = asNullableString(body.framing_terminator);
    if (typeof body.max_frame_bytes === "number") view.max_frame_bytes = body.max_frame_bytes;
    view.conversion_profile_ref = asNullableString(body.conversion_profile_ref);
    // Defaulted to `[]` rather than left undefined: the projection always writes
    // the key, so its absence is drift — and on this field an empty list is a
    // real, accepted state that the UI must be able to render and say so about.
    view.demarcation_ips = Array.isArray(body.demarcation_ips)
      ? body.demarcation_ips.filter((ip): ip is string => typeof ip === "string")
      : [];
    view.provider_ca_bundle_ref = asNullableString(body.provider_ca_bundle_ref);
    view.meridian_cert_ref = asNullableString(body.meridian_cert_ref);
    view.meridian_key_ref = asNullableString(body.meridian_key_ref);
    view.filter = isRecord(body.filter) ? (body.filter as ProviderFilterConfig) : null;
    if (typeof body.schema_version === "number") view.schema_version = body.schema_version;
    if (typeof body.created_by === "string") view.created_by = body.created_by;
  }
  return view;
}

/**
 * Coerce a wire ``state`` token, **failing safe to ``stopped``**.
 *
 * An unrecognized token must never read as ``started``: the pill is how an
 * operator confirms a feed they stopped is actually stopped, and guessing
 * "running" on drift would be a false negative on the one control this surface
 * provides (CLAUDE.md §8.1).
 *
 * @throws Error when the field is absent entirely — that is drift, not a value.
 */
function parseInterfaceState(value: unknown, context: string): InterfaceState {
  if (typeof value !== "string") {
    throw new Error(`provider ${context}: state must be a string`);
  }
  return value === "started" ? "started" : "stopped";
}

function parseProviderConnection(
  body: unknown,
  interfaceName: string,
): ProviderInterfaceConnectionView {
  if (!isRecord(body)) {
    throw new Error(`provider interface ${interfaceName}: malformed connection`);
  }
  const { connection_id, name, role } = body;
  if (typeof connection_id !== "string" || typeof name !== "string") {
    throw new Error(`provider interface ${interfaceName}: connection_id and name must be strings`);
  }
  const view: ProviderInterfaceConnectionView = {
    connection_id,
    name,
    state: parseInterfaceState(body.state, `connection ${name}`),
    // `listen` is the ingress default and the fail-safe reading of an
    // unrecognised token: it is the only role any transport realizes today, so
    // guessing `dial_out` would describe a connection that cannot exist.
    role: role === "dial_out" ? "dial_out" : "listen",
  };
  if (typeof body.endpoint_port === "number") {
    view.endpoint_host = asNullableString(body.endpoint_host);
    view.endpoint_port = body.endpoint_port;
    view.timeout_seconds = typeof body.timeout_seconds === "number" ? body.timeout_seconds : 30;
    view.retry_max_attempts =
      typeof body.retry_max_attempts === "number" ? body.retry_max_attempts : 3;
    view.retry_backoff_seconds =
      typeof body.retry_backoff_seconds === "number" ? body.retry_backoff_seconds : 1;
    view.client_fingerprint_sha256 = asNullableString(body.client_fingerprint_sha256);
    view.filter = isRecord(body.filter) ? (body.filter as ProviderFilterConfig) : null;
  }
  return view;
}

/**
 * Parse the wire-security mode, refusing anything the client does not recognise.
 *
 * Strict on purpose. A default here would be indistinguishable from the server
 * actually reporting that value, and the field decides whether mission data
 * arrives in the clear — so an unrecognised mode is a client/server
 * disagreement to surface, not one to paper over.
 */
function parseTransportSecurity(value: unknown, name: string): TransportSecurity {
  if (value === "plaintext" || value === "tls" || value === "mtls") return value;
  throw new Error(
    `provider interface ${name}: unrecognised transport_security ${JSON.stringify(value)}`,
  );
}

/**
 * Parse the attribution mode, refusing anything the client does not recognise.
 *
 * Strict for {@link parseTransportSecurity}'s reason and a second one: this
 * value round-trips into the PUT body, so a defaulted ``mtls`` would be written
 * back and would claim Meridian authenticates a source the network layer
 * actually admits — a false statement about who verified the counterparty.
 */
function parseTrustMode(value: unknown, name: string): TrustMode {
  if (value === "mtls" || value === "tunnel") return value;
  throw new Error(`provider interface ${name}: unrecognised trust_mode ${JSON.stringify(value)}`);
}

/**
 * Parse the conversion mode, refusing anything the client does not recognise.
 *
 * Strict because the value round-trips: a defaulted ``legacy`` written back
 * would demote a promoted interface to the built-in decoder on an unrelated
 * save, and nothing would say so.
 */
function parseConversionMode(value: unknown, name: string): ConversionMode {
  if (value === "legacy" || value === "engine") return value;
  throw new Error(
    `provider interface ${name}: unrecognised conversion_mode ${JSON.stringify(value)}`,
  );
}

/**
 * Parse the filter summary, failing safe to *unconfigured* when it is absent or
 * malformed.
 *
 * ⚠ On ingress the safe guess and the conservative one **diverge**, and this
 * takes the safe one deliberately. ``unconfigured`` here reads as *receives
 * everything*, which is the wider posture — but it is also exactly what the
 * pipeline does when it cannot read a document (the compiler falls back to the
 * direction's fail-safe, allow-all), so it is the *honest* report. Guessing
 * "configured" would tell an operator a feed is narrowed when the UI has no idea,
 * and on ingress a false "narrowed" reading is what hides silent data loss.
 */
function parseFilterSummary(value: unknown): ProviderFilterSummary {
  if (!isRecord(value)) return { configured: false, clause_count: 0, selector_kinds: [] };
  return {
    configured: value.configured === true,
    clause_count: typeof value.clause_count === "number" ? value.clause_count : 0,
    selector_kinds: Array.isArray(value.selector_kinds)
      ? value.selector_kinds.filter((k): k is string => typeof k === "string")
      : [],
  };
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

// ---- Read → edit round-trip ------------------------------------------------

/**
 * Rebuild the PUT body from a privileged read projection.
 *
 * The mutation surface is a whole-document PUT, so editing one field means
 * re-sending every other one; this is the lossless inverse of the BFF's
 * ``_interface_to_dict(full=True)``. The one reshaping is the retry policy,
 * which the read flattens (``retry_max_attempts`` / ``retry_backoff_seconds``)
 * and the config nests (``retry: {max_attempts, backoff_seconds}``).
 *
 * @throws Error when handed a non-privileged view — a PUT built from the
 *   field-limited projection would blank the bind endpoints, demarcation IPs,
 *   cert references and filter of a live ingest path. Refusing is the only safe
 *   reading; the UI never offers the editor for such a session anyway (defence
 *   in depth).
 */
export function toProviderInterfaceConfig(view: ProviderInterfaceView): ProviderInterfaceConfig {
  if (!isPrivilegedProviderInterfaceView(view)) {
    throw new Error(
      "toProviderInterfaceConfig: refusing to build a write body from a field-limited read view",
    );
  }
  return {
    interface_id: view.interface_id,
    name: view.name,
    type_id: view.type_id,
    enabled: view.enabled,
    state: view.state,
    // Both axes are carried through from the read, never defaulted. Defaulting
    // `transport_security` would silently rewrite a live interface's WIRE
    // SECURITY on any unrelated edit; defaulting `trust_mode` would silently
    // claim Meridian authenticates a source the network layer actually admits.
    // Both are in the all-roles block precisely so this round-trip works.
    transport_security: view.transport_security,
    trust_mode: view.trust_mode,
    // The tunnel record. Carried through so an unrelated edit cannot erase the
    // operational note of where the protected path admits from — an empty list
    // is a valid state, so a dropped one is indistinguishable from a real one.
    demarcation_ips: view.demarcation_ips ?? [],
    // Carried for the same reason, with a sharper failure: dropping the ref
    // while keeping a non-`legacy` mode is a guaranteed 422 (the model's
    // `conv_needs_profile` CHECK), so the operator would find an unrelated field
    // uneditable with no indication why. Both are always present on a privileged
    // view, which this function has already required.
    conversion_profile_ref: view.conversion_profile_ref ?? null,
    conversion_mode: view.conversion_mode,
    // The transport selection must round-trip: `raw-data-ingress` is the one
    // type with a choice (`mtls-tcp-listen` | `zeromq-pull`), and resetting it
    // to the type default on an unrelated edit would move a live aggregator's
    // raw channel onto a different listener.
    transport_id: view.transport_id ?? null,
    // Wire framing must round-trip. Dropping these would silently reset an
    // interface's framing to the default on any *unrelated* edit — the provider
    // would stop being parseable and nothing in the UI would have said so.
    framing: view.framing ?? "length_prefix",
    framing_delimiter_hex: view.framing_delimiter_hex ?? "1e",
    framing_terminator: view.framing_terminator ?? null,
    max_frame_bytes: view.max_frame_bytes ?? DEFAULT_MAX_FRAME_BYTES,
    provider_ca_bundle_ref: view.provider_ca_bundle_ref ?? null,
    meridian_cert_ref: view.meridian_cert_ref ?? null,
    meridian_key_ref: view.meridian_key_ref ?? null,
    filter: view.filter ?? null,
    connections: view.connections.map(toProviderConnectionConfig),
    metadata: view.metadata ?? {},
    schema_version: view.schema_version ?? PROVIDER_SCHEMA_VERSION,
  };
}

/**
 * Rebuild one connection's PUT shape from its read projection.
 *
 * ⚠ **``endpoint_host`` is kept under both roles**, unlike the egress twin,
 * because the ingress model's meanings differ: under ``listen`` it is the
 * *optional local bind address* (absent means every interface) and under
 * ``dial_out`` it is *required*. Clearing it on ``listen`` would silently
 * re-bind a deliberately-narrowed listener to `0.0.0.0` on an unrelated save.
 * The **editor** clears it on a role *switch* — a different act, and one the
 * operator performed (Flow V Frame 5).
 */
export function toProviderConnectionConfig(
  view: ProviderInterfaceConnectionView,
): ProviderInterfaceConnectionConfig {
  return {
    connection_id: view.connection_id,
    name: view.name,
    role: view.role,
    endpoint_host: view.endpoint_host ?? null,
    endpoint_port: view.endpoint_port ?? DEFAULT_PROVIDER_PORT,
    state: view.state,
    timeout_seconds: view.timeout_seconds ?? 30,
    retry: {
      max_attempts: view.retry_max_attempts ?? 3,
      backoff_seconds: view.retry_backoff_seconds ?? 1,
    },
    client_fingerprint_sha256: view.client_fingerprint_sha256 ?? null,
    filter: view.filter ?? null,
  };
}

/**
 * A brand-new interface, **stopped** (REQ-PROVIDER-007) and with **no filter**.
 *
 * ⚠ No filter means it **receives everything** (REQ-PROVIDER-037) — the
 * deliberate inverse of the egress default, and the fail-safe one here. Dropping
 * sensor data at the perimeter is unrecoverable: the record exists nowhere else
 * and no later act reconstructs it. Over-collection is a disclosure risk that
 * other controls address; under-collection is silence that nothing detects.
 *
 * Both axes come **from the type**, not from a blanket default: a CDS interface
 * created `mtls`/`mtls` is one the BFF refuses with a 422, so the create form
 * would offer a shape the server will not accept. The type's own defaults are
 * fail-safe by construction (`mtls` wherever the type permits it).
 */
export function newProviderInterfaceConfig(
  interfaceId: string,
  typeId: string,
): ProviderInterfaceConfig {
  const descriptor = findProviderInterfaceType(typeId);
  return {
    interface_id: interfaceId,
    name: "",
    type_id: typeId,
    enabled: true,
    state: "stopped",
    trust_mode: descriptor?.defaultTrustMode ?? "mtls",
    transport_security: descriptor?.defaultTransportSecurity ?? "mtls",
    demarcation_ips: [],
    // No conversion, built-in decoder. The fail-safe pair, and the only one a
    // create form could offer: a profile has to be installed before it can be
    // referenced, so a new interface cannot name one, and a non-`legacy` mode
    // without a ref is a 422.
    conversion_profile_ref: null,
    conversion_mode: "legacy",
    // Null means the type's default listener — the common case, and the only
    // honest value for a type with no choice.
    transport_id: null,
    framing: "length_prefix",
    framing_delimiter_hex: "1e",
    framing_terminator: null,
    max_frame_bytes: DEFAULT_MAX_FRAME_BYTES,
    provider_ca_bundle_ref: null,
    meridian_cert_ref: null,
    meridian_key_ref: null,
    filter: null,
    connections: [],
    metadata: {},
    schema_version: PROVIDER_SCHEMA_VERSION,
  };
}

/** A brand-new provider connection: **stopped**, and Meridian listens (ADR-040 §2.5 default). */
export function newProviderConnectionConfig(
  connectionId: string,
): ProviderInterfaceConnectionConfig {
  return {
    connection_id: connectionId,
    name: "",
    role: "listen",
    // Blank bind address = every interface, which is the listener default and
    // what an operator who has not thought about it wants.
    endpoint_host: null,
    endpoint_port: DEFAULT_PROVIDER_PORT,
    state: "stopped",
    timeout_seconds: 30,
    retry: { max_attempts: 3, backoff_seconds: 1 },
    client_fingerprint_sha256: null,
    filter: null,
  };
}

// ---- Providers (ADR-040 §D-1) ----------------------------------------------

/**
 * One provider as the roster endpoint projects it.
 *
 * The same two-shapes-from-one-endpoint rule as {@link ProviderInterfaceView}:
 * every authenticated role sees that the provider exists and whether it is
 * collecting — a silenced feed an operator cannot see is worse than the
 * silencing — while ``metadata`` and ``created_by`` are privileged.
 *
 * ``interfaces`` is deliberately absent rather than empty. The roster and a
 * provider's interface list are separate reads, and an always-empty key here
 * would read as "this provider has no interfaces" to anyone who did not know to
 * look elsewhere.
 */
/** One producer row that is a provider's own declared node. */
export interface ProviderNodeProducer {
  /** The ``control_plane.producers`` row id — the join target. */
  producer_id: string;
  /** The wire identity the node declared, as the producers inventory shows it. */
  display_name: string;
}

export interface ProviderView {
  provider_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  state: InterfaceState;
  schema_version?: number;
  /**
   * How many interfaces this provider owns, and how many are started.
   *
   * Served by the **list** route only, so both are optional and are
   * ``undefined`` — never 0 — on a single-provider read. On ingress a guessed
   * zero reads as "nothing is arriving through this provider", which is an alarm
   * the row is in no position to raise (REQ-UI-156).
   */
  interface_count?: number;
  started_interface_count?: number;
  /**
   * The producer rows that are this provider's **own** node — the identity the
   * external system declared for itself at handshake, auto-registered like any
   * producer id but not a sensor platform.
   *
   * Answers "this provider's node is producer row X" (ADR-040 §D-1). A **list**
   * because one provider may own several interfaces and nothing stops two of them
   * declaring different identities; ordinarily empty or one.
   *
   * ``undefined`` means the BFF did not resolve it. An **empty array** is a real and
   * different answer — a provider whose interfaces carry no handshake (raw, CDS)
   * never declares a node, and neither does one that has not been connected to yet.
   */
  node_producers?: ProviderNodeProducer[];
  // ---- privileged-only (absent for operator / analyst) ----
  /**
   * Operator free-form annotations.
   *
   * Typed ``Record<string, unknown>`` on the **read**, not
   * ``Record<string, string>`` like the write body's — deliberately permissive.
   * ``metadata`` is a JSONB column an operator and a migration both write into,
   * so the read type describes what a row *can* hold rather than what the write
   * schema accepts, and the narrowing happens once, at
   * {@link toProviderConfig}.
   *
   * (Migration 066 briefly made this load-bearing: it wrote a nested object under
   * ``projected_from_aggregator``, which the write model refuses. Migration 068
   * flattened it into dotted string keys. The permissive read type is what let
   * the surface keep rendering those providers correctly in between.)
   */
  metadata?: Record<string, unknown>;
  created_by?: string;
}

/**
 * Whether the provider response carried the privileged field-set.
 *
 * Keyed on ``metadata`` for the same reason
 * {@link isPrivilegedProviderInterfaceView} is: ``_provider_to_dict`` writes it
 * inside the ``full`` block unconditionally (``dict(record.metadata)`` —
 * possibly empty, never omitted), so unlike ``created_by`` it cannot be
 * absent-because-null.
 */
export function isPrivilegedProviderView(view: ProviderView): boolean {
  return view.metadata !== undefined;
}

/**
 * Whether this provider was projected out of the retired
 * ``control_plane.aggregators`` registry by migration 066.
 *
 * Such a provider carries **no interfaces** — an aggregator row held identity
 * and trust facts only, never an endpoint or a type — so without this the row is
 * indistinguishable from a provider whose interfaces someone deleted, and its
 * empty Interfaces section would read as an alarm rather than as a to-do (Flow V
 * Frame 2).
 *
 * Returns ``false`` for a non-privileged view, which never carries ``metadata``:
 * the projection marker is privileged like the rest of it, and guessing would be
 * the client re-deriving a field-set the BFF already decided.
 */
export function isProjectedProvider(view: ProviderView): boolean {
  return view.metadata?.[PROJECTED_FROM_AGGREGATOR_KEY] !== undefined;
}

/**
 * Parse the provider roster body.
 *
 * @throws Error when the body is not ``{items: [...]}`` of well-formed providers.
 */
export function parseProviderList(body: unknown): ProviderView[] {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error("provider list: expected an object with an `items` array");
  }
  return body.items.map(parseProvider);
}

/**
 * Parse one provider object.
 *
 * Rejects rather than repairs, for the reason {@link parseProviderInterface}
 * does: a provider silently dropped from the roster is a system an operator
 * cannot see feeding the deployment, and one with a guessed ``state`` is a kill
 * switch showing the wrong position.
 *
 * @throws Error when a field every role is promised is missing or mistyped.
 */
function isProviderNodeProducer(value: unknown): value is ProviderNodeProducer {
  return (
    isRecord(value) &&
    typeof value.producer_id === "string" &&
    typeof value.display_name === "string"
  );
}

export function parseProvider(body: unknown): ProviderView {
  if (!isRecord(body)) throw new Error("provider: expected an object");
  const { provider_id, name, enabled, metadata } = body;
  if (typeof provider_id !== "string" || typeof name !== "string") {
    throw new Error("provider: provider_id and name must be strings");
  }
  if (typeof enabled !== "boolean") {
    throw new Error(`provider ${name}: enabled must be a boolean`);
  }
  const view: ProviderView = {
    provider_id,
    name,
    description: asNullableString(body.description),
    enabled,
    state: parseInterfaceState(body.state, `provider ${name}`),
  };
  if (typeof body.schema_version === "number") view.schema_version = body.schema_version;
  if (typeof body.interface_count === "number") view.interface_count = body.interface_count;
  if (Array.isArray(body.node_producers)) {
    // Drop malformed entries rather than the whole provider: a bad provenance entry
    // must not remove a system from the roster an operator uses to stop it.
    view.node_producers = body.node_producers.filter(isProviderNodeProducer);
  }
  if (typeof body.started_interface_count === "number") {
    view.started_interface_count = body.started_interface_count;
  }
  if (isRecord(metadata)) {
    view.metadata = metadata;
    if (typeof body.created_by === "string") view.created_by = body.created_by;
  }
  return view;
}

/**
 * Rebuild the provider PUT body from a read projection.
 *
 * ``interfaces`` is sent empty and that is not a loss: the provider PUT
 * deliberately does not write its interface set, so a rename cannot silently
 * delete everything beneath it. Interfaces are edited through their own routes
 * (REQ-PROVIDER-016 — the audit record names the full path).
 *
 * **Metadata is carried verbatim, never edited on the way through.** That is the
 * round-trip discipline: this client did not author those keys and cannot know
 * which of them another surface depends on.
 *
 * The rule was briefly load-bearing for a real defect. Migration 066 wrote the
 * projected aggregator's facts as a *nested* object under
 * ``projected_from_aggregator``, while ``ProviderConfig.metadata`` is
 * ``Mapping[str, str]`` server-side — so a projected provider's round-trip 422'd
 * and it could not be renamed at all. **Migration 068 fixed that at the source**
 * by flattening the projection into dotted string keys, so the shape now
 * satisfies the model. Preserving rather than dropping is what kept the
 * provenance intact until it did.
 *
 * @throws Error when handed a non-privileged view — a PUT built from the
 *   field-limited projection would blank the provider's operator annotations.
 */
export function toProviderConfig(view: ProviderView): ProviderConfig {
  if (!isPrivilegedProviderView(view)) {
    throw new Error(
      "toProviderConfig: refusing to build a write body from a field-limited read view",
    );
  }
  return {
    provider_id: view.provider_id,
    name: view.name,
    description: view.description,
    enabled: view.enabled,
    state: view.state,
    interfaces: [],
    // Cast rather than coerce. The read type stays permissive on purpose — see
    // the field's own note — while the write schema is the narrow one, and the
    // server is the authority on which it will accept. Losing a value is the one
    // outcome that cannot be undone, so nothing is dropped here.
    metadata: (view.metadata ?? {}) as ProviderConfig["metadata"],
    schema_version: view.schema_version ?? PROVIDER_SCHEMA_VERSION,
  };
}

/**
 * A brand-new provider, **stopped** (REQ-PROVIDER-007).
 *
 * Stopped is the fail-safe default and is not offered as a choice at create
 * time, matching {@link newProviderInterfaceConfig}: creating a provider and
 * beginning to collect from it are two decisions, and a create that could start
 * collecting in the same click would collapse them into one.
 */
export function newProviderConfig(providerId: string): ProviderConfig {
  return {
    provider_id: providerId,
    name: "",
    description: null,
    enabled: true,
    state: "stopped",
    interfaces: [],
    metadata: {},
    schema_version: PROVIDER_SCHEMA_VERSION,
  };
}
